import axios, { type CancelTokenSource } from 'axios';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  type JSX,
} from 'react';
import backButtonDark from '@/assets/svg/static/back-button-dark.svg';
import {
  DoubleColumnProductList,
  FilterBar,
  type PriceFilterTags,
} from '@/components/features';
import { ScrollableTabs } from '@/components/ui';
import { PRICE_RANGES } from '@/constants/priceFilterRanges';
import {
  fetchCategoryProducts,
  fetchSubcategoryProducts,
} from '@/services/slaydAPI';
import useCategoryStore from '@/stores/CategoryStore';
import useLayoutStore from '@/stores/useLayoutStore';
import type {
  CategoryItem,
  CategoryPagination,
  CategoryProduct,
  SubcategoryItem,
} from '@/types/Category.types';
import { getMinMaxPriceRange, toTitleCase } from '@/utils/formatting';
import {
  ALL,
  ALL_PRODUCT_BOX,
  ITEMS_UNTIL_LOAD_MORE,
  LAST_ITEMS_LOAD_MORE_THRESHOLD,
  SCROLL_HIDE_HEADER_THRESHOLD,
} from './CategoryPage.constants';
import styles from './CategoryPage.module.css';
import type { CategoryCenteredItem } from './CategoryPage.types';

const MIN_PRICE_URL_QUERY = 'min';
const MAX_PRICE_URL_QUERY = 'max';

const CategoryPage = (): JSX.Element => {
  const navigate = useNavigate();
  const { id: categoryName } = useParams();
  const location = useLocation();
  const subcategorySlug = location.hash.substring(1);
  const {
    getCachedData,
    // storeCurrentQuery, // unused
  } = useCategoryStore();
  const [categoryproducts, setCategoryProducts] = useState<CategoryProduct[]>(
    []
  );
  const [mainCategoryProducts, setMainCategoryProducts] = useState<
    CategoryProduct[]
  >([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [priceFilters, setPriceFilters] =
    useState<PriceFilterTags[]>(PRICE_RANGES);
  const [isTitleHidden, setIsTitleHidden] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [isFetchingMore, setIsFetchingMore] = useState<boolean>(false);
  const [onSale, setOnSale] = useState<boolean>(false);
  const setHide = useLayoutStore((state) => state.setHide);
  const lastScrollTop = useRef(0);
  const cancelTokenRef = useRef<CancelTokenSource | null>(null);
  const keepLoadingRef = useRef<boolean>(false);

  // Scroll
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const cacheKey = location.pathname + location.search;
  const categoriesObj = JSON.parse(
    sessionStorage.getItem('slayd_categories') || '{}'
  );
  const categories = Array.isArray(categoriesObj.categories)
    ? categoriesObj.categories
    : [];
  const matchedCategory = (categories as CategoryItem[]).find(
    (cat: CategoryItem) => String(cat.slug) === String(categoryName)
  );
  const scrollableItems = [
    ALL_PRODUCT_BOX,
    ...(matchedCategory?.subcategories ?? []),
  ];

  let initialActiveIndex = 0;
  if (subcategorySlug) {
    const foundIdx = scrollableItems.findIndex(
      (item) => item.slug === subcategorySlug
    );
    if (foundIdx !== -1) initialActiveIndex = foundIdx;
  }

  const smoothScrollToTop = useCallback(() => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const onScroll = (): void => {
        if (container.scrollTop <= 2) {
          setIsTitleHidden(false);
          setHide(false);
          container.removeEventListener('scroll', onScroll);
        }
      };
      container.addEventListener('scroll', onScroll);
      container.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
      lastScrollTop.current = 0;
    }
  }, [setHide]);

  // Sync onSale state with URL param
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const newSaleState = searchParams.get('sale') === 'true';
    setOnSale(newSaleState);
  }, [location.search]);

  // Update fetchCategory to accept and use sale param
  const fetchCategory = useCallback(
    async (
      page: number = 1,
      append: boolean = false,
      saleOverride?: boolean
    ): Promise<void> => {
      // Cancel existing request if any
      if (cancelTokenRef.current) {
        keepLoadingRef.current = true;
        cancelTokenRef.current.cancel('New request started');
        cancelTokenRef.current = null;
      }

      // Create new cancel token for this request
      cancelTokenRef.current = axios.CancelToken.source();

      if (page >= 1) {
        setIsFetchingMore(true);
      } else {
        setLoading(true);
      }
      try {
        // Always get the current price and sale state from URL to avoid stale state
        const searchParams = new URLSearchParams(location.search);
        const currentSaleState = searchParams.get('sale') === 'true';
        const sale =
          typeof saleOverride === 'boolean' ? saleOverride : currentSaleState;

        // Get price filters from URL
        const minPriceFromUrl = searchParams.get(MIN_PRICE_URL_QUERY);
        const maxPriceFromUrl = searchParams.get(MAX_PRICE_URL_QUERY);
        const selected = minPriceFromUrl !== null && maxPriceFromUrl !== null;
        const minPrice = minPriceFromUrl ? parseInt(minPriceFromUrl, 10) : 0;
        const maxPrice = maxPriceFromUrl ? parseInt(maxPriceFromUrl, 10) : 0;
        let data;
        const extraParams = sale ? { sale: true } : {};
        if (subcategorySlug) {
          data = await fetchSubcategoryProducts(
            subcategorySlug,
            selected
              ? {
                  page,
                  minPrice,
                  maxPrice,
                  ...extraParams,
                  config: {
                    cancelToken: cancelTokenRef.current
                      ? (cancelTokenRef.current as CancelTokenSource).token
                      : undefined,
                  },
                }
              : {
                  page,
                  ...extraParams,
                  config: {
                    cancelToken: cancelTokenRef.current
                      ? (cancelTokenRef.current as CancelTokenSource).token
                      : undefined,
                  },
                }
          );
        } else {
          data = await fetchCategoryProducts(
            location.pathname,
            selected
              ? {
                  page,
                  minPrice,
                  maxPrice,
                  ...extraParams,
                  config: {
                    cancelToken: cancelTokenRef.current
                      ? (cancelTokenRef.current as CancelTokenSource).token
                      : undefined,
                  },
                }
              : {
                  page,
                  ...extraParams,
                  config: {
                    cancelToken: cancelTokenRef.current
                      ? (cancelTokenRef.current as CancelTokenSource).token
                      : undefined,
                  },
                }
          );
        }
        const products: CategoryProduct[] = data.products || [];
        const pagination: CategoryPagination | undefined = data.pagination;

        if (subcategorySlug) {
          setCategoryProducts((prev) =>
            append ? [...prev, ...products] : products
          );
        } else {
          setMainCategoryProducts((prev) =>
            append ? [...prev, ...products] : products
          );
          setCategoryProducts((prev) =>
            append ? [...prev, ...products] : products
          );
        }

        if (pagination) {
          setCurrentPage(pagination.currentPage);
          setHasMore(pagination.hasNext);
        } else {
          setHasMore(false);
        }

        if (!append) {
          setTimeout(() => {
            smoothScrollToTop();
          }, 0);
        }
      } catch (err) {
        console.error(err);
        setHasMore(false);
      } finally {
        keepLoadingRef.current = false;
        if (page >= 1) {
          setIsFetchingMore(false);
        } else {
          setLoading(false);
        }
        if (cancelTokenRef.current) {
          cancelTokenRef.current = null;
        }
      }
    },
    [subcategorySlug, location.pathname, location.search, smoothScrollToTop]
  );

  const handleCategoryClick = (item: CategoryCenteredItem): void => {
    // Cancel existing request if any
    if (cancelTokenRef.current) {
      keepLoadingRef.current = true;
      cancelTokenRef.current.cancel('Category change request started');
      cancelTokenRef.current = null;
    }

    if (item.slug === ALL) {
      navigate(location.pathname, { replace: true });
      setCategoryProducts(mainCategoryProducts);
      setTimeout(() => {
        smoothScrollToTop();
      }, 0);
    } else {
      navigate(`${location.pathname}#${item.slug}`, {
        replace: true,
      });
      setTimeout(() => {
        smoothScrollToTop();
      }, 0);
    }
  };

  // Consolidated useEffect to handle initial load and URL changes
  useEffect((): void => {
    fetchCategory(1, false);
  }, [cacheKey, getCachedData, fetchCategory]);

  let titleText = toTitleCase(categoryName ?? '');
  if (subcategorySlug) {
    const allSubcategories = matchedCategory?.subcategories?.flat?.() ?? [];
    const subcatObj = (allSubcategories as SubcategoryItem[]).find(
      (item: SubcategoryItem) => item.slug === subcategorySlug
    );
    if (subcatObj) {
      titleText = toTitleCase(subcatObj.name);
    } else {
      titleText = toTitleCase(subcategorySlug.replace(/-/g, ' '));
    }
  }

  // Update pagination
  const handleLoadMore = useCallback(() => {
    if (hasMore && !isFetchingMore) {
      fetchCategory(currentPage + 1, true);
    }
  }, [hasMore, isFetchingMore, fetchCategory, currentPage]);

  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const scrollTop = event.currentTarget.scrollTop;

      if (
        scrollTop > lastScrollTop.current &&
        scrollTop > SCROLL_HIDE_HEADER_THRESHOLD
      ) {
        setIsTitleHidden(true);
        setHide(true);
      } else if (scrollTop < lastScrollTop.current) {
        setIsTitleHidden(false);
        setHide(false);
      }
      lastScrollTop.current = scrollTop;

      if (hasMore && !isFetchingMore && categoryproducts.length > 0) {
        const container = event.currentTarget;
        const productItems = container.querySelectorAll('[data-product-item]');

        if (productItems.length > 0) {
          const lastItems = Array.from(productItems).slice(
            -ITEMS_UNTIL_LOAD_MORE
          );

          const isAnyLastItemApproaching = lastItems.some((item) => {
            const rect = item.getBoundingClientRect();
            const containerRect = container.getBoundingClientRect();

            return (
              rect.bottom <=
                containerRect.bottom + LAST_ITEMS_LOAD_MORE_THRESHOLD &&
              rect.top >= containerRect.top - LAST_ITEMS_LOAD_MORE_THRESHOLD
            );
          });

          if (isAnyLastItemApproaching) {
            handleLoadMore();
          }
        }
      }
    },
    [hasMore, isFetchingMore, categoryproducts.length, setHide, handleLoadMore]
  );

  // On Sale pill toggle handler
  const handleSaleToggle = useCallback(() => {
    // Cancel existing request if any
    if (cancelTokenRef.current) {
      keepLoadingRef.current = true;
      cancelTokenRef.current.cancel('Sale toggle request started');
      cancelTokenRef.current = null;
    }

    setCategoryProducts([]);
    setMainCategoryProducts([]);
    setCurrentPage(1);

    // Update URL - this will trigger the useEffect that depends on onSale
    const searchParams = new URLSearchParams(location.search);
    if (!onSale) {
      searchParams.set('sale', 'true');
    } else {
      searchParams.delete('sale');
    }
    navigate(
      `${location.pathname}?${searchParams.toString()}${location.hash}`,
      { replace: true }
    );
  }, [location.search, location.pathname, location.hash, navigate, onSale]);

  const handlePriceFilterChange = useCallback(
    (selectedFilters: PriceFilterTags[]) => {
      if (cancelTokenRef.current) {
        keepLoadingRef.current = true;
        cancelTokenRef.current.cancel('Price filter change request started');
        cancelTokenRef.current = null;
      }

      const { minPrice: minPriceOld, maxPrice: maxPriceOld } =
        getMinMaxPriceRange(priceFilters);
      const { minPrice: minPriceNew, maxPrice: maxPriceNew } =
        getMinMaxPriceRange(selectedFilters);

      if (minPriceOld !== minPriceNew || maxPriceOld !== maxPriceNew) {
        setCategoryProducts([]);
        setMainCategoryProducts([]);
        setCurrentPage(1);
      }

      setPriceFilters(selectedFilters);

      const searchParams = new URLSearchParams(location.search);
      searchParams.set(MIN_PRICE_URL_QUERY, minPriceNew.toString());
      searchParams.set(MAX_PRICE_URL_QUERY, maxPriceNew.toString());
      if (onSale) searchParams.set('sale', 'true');
      else searchParams.delete('sale');
      navigate(
        `${location.pathname}?${searchParams.toString()}${location.hash}`,
        {
          replace: true,
        }
      );
    },
    [
      priceFilters,
      location.search,
      location.pathname,
      location.hash,
      navigate,
      onSale,
    ]
  );

  // Clean up cancel token on unmount
  useEffect(() => {
    return () => {
      if (cancelTokenRef.current) {
        cancelTokenRef.current.cancel('Component unmounted');
        cancelTokenRef.current = null;
      }
    };
  }, []);

  return (
    <div className={styles.container}>
      <div
        className={`${styles.headerGroup} ${isTitleHidden ? styles.headerGroupHidden : styles.hidden}`}
      >
        <div
          className={`${styles.titleContainer} ${isTitleHidden ? styles.hideTitle : ''}`}
        >
          <h2 className={styles.title}>{titleText}</h2>
        </div>
        <div className={styles.scrollableBarContainer}>
          <ScrollableTabs
            items={scrollableItems}
            initialActiveIndex={initialActiveIndex}
            shiftTabs={isTitleHidden}
            onClick={handleCategoryClick}
            onCenteredItemChange={() => {}}
          />
        </div>
        <div className={styles.filterBarContainer}>
          <FilterBar
            filters={[]}
            priceFilter={priceFilters}
            isSaleSelected={onSale}
            onFilterClick={() => {}}
            onPriceFilterChange={handlePriceFilterChange}
            onSaleToggle={handleSaleToggle}
          />
        </div>
      </div>
      <div
        ref={scrollContainerRef}
        className={styles.scrollableProductListContainer}
        onScroll={handleScroll}
      >
        <DoubleColumnProductList
          products={categoryproducts.map(
            ({ subcategory, description, wishlists, ...rest }) => ({
              ...rest,
              subcategory: subcategory === null ? undefined : subcategory,
              description: description === null ? undefined : description,
              wishlists: Array.isArray(wishlists) ? wishlists : undefined,
            })
          )}
          className={styles.productListContainer}
          hasMore={hasMore}
          isLoading={loading || isFetchingMore}
          onLoadMore={() => {}}
        />
      </div>
      <button
        className={styles.fixedBackButton}
        onClick={() => {
          navigate(-1);
        }}
      >
        <img src={backButtonDark} alt="Back" className={styles.backIcon} />
      </button>
    </div>
  );
};

export default CategoryPage;
